import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  ChevronLeft,
  ChevronRight,
  Download,
  History,
  Lock,
  LogOut,
  Plus,
  RefreshCw,
  Scissors,
  Trash2,
} from "lucide-react";
import { fullDate, maskPhone } from "@/components/Booking";
import { WhatsAppIcon } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "si_admin_token";

const waLink = (phone) => {
  const digits = phone.replace(/\D/g, "");
  return `https://wa.me/${digits.length <= 11 ? `55${digits}` : digits}`;
};

const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
const monthLabelPT = (d) => {
  const m = new Intl.DateTimeFormat("pt-BR", { month: "long" }).format(d);
  return `${m.charAt(0).toUpperCase()}${m.slice(1)} ${d.getFullYear()}`;
};

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [checking, setChecking] = useState(Boolean(localStorage.getItem(TOKEN_KEY)));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [bookings, setBookings] = useState(null);
  const [tab, setTab] = useState("proximos");
  const [histMonth, setHistMonth] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d;
  });
  const [histData, setHistData] = useState(null);
  const [histLoading, setHistLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [addDate, setAddDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [addSlots, setAddSlots] = useState(null);
  const [addTime, setAddTime] = useState(null);
  const [addName, setAddName] = useState("");
  const [addPhone, setAddPhone] = useState("");
  const [addLoading, setAddLoading] = useState(false);

  const authHeaders = (t = token) => ({ headers: { Authorization: `Bearer ${t}` } });

  const handleAuthError = (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem(TOKEN_KEY);
      setToken(null);
      toast.error("Sessão expirada. Entre novamente.");
      return true;
    }
    return false;
  };

  const loadBookings = async (t = token, silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const { data } = await axios.get(`${API}/admin/bookings`, authHeaders(t));
      setBookings(data.bookings);
    } catch (err) {
      if (!handleAuthError(err)) toast.error("Não foi possível carregar a agenda.");
    } finally {
      setRefreshing(false);
    }
  };

  const loadHistory = async (monthDate) => {
    setHistLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/bookings/history`, {
        ...authHeaders(),
        params: { month: monthKey(monthDate) },
      });
      setHistData(data);
    } catch (err) {
      if (!handleAuthError(err)) toast.error("Não foi possível carregar o histórico.");
    } finally {
      setHistLoading(false);
    }
  };

  useEffect(() => {
    if (!token) {
      setChecking(false);
      return;
    }
    axios
      .get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => {
        setChecking(false);
        loadBookings(token, true);
      })
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        setChecking(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (token && tab === "historico") loadHistory(histMonth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, histMonth]);

  const login = async () => {
    if (!email.trim() || !password) {
      toast.error("Preencha e-mail e senha.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, {
        email: email.trim(),
        password,
      });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      toast.success(`Bem-vindo, ${data.name}!`);
      loadBookings(data.token, true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Não foi possível entrar.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setBookings(null);
    setHistData(null);
    setEmail("");
    setPassword("");
  };

  const cancelBooking = async (b) => {
    if (!window.confirm(`Cancelar o agendamento de ${b.name} (${b.date} às ${b.time})?`)) return;
    try {
      await axios.delete(`${API}/admin/bookings/${b.id}`, authHeaders());
      setBookings((prev) => prev.filter((item) => item.id !== b.id));
      toast.success(`Horário de ${b.name} cancelado e liberado.`);
    } catch (err) {
      if (!handleAuthError(err)) toast.error("Não foi possível cancelar.");
    }
  };

  const exportMonth = () => {
    if (!histData || histData.bookings.length === 0) {
      toast.error("Nenhum atendimento neste mês para exportar.");
      return;
    }
    const rows = [...histData.bookings].sort((a, b) =>
      `${a.date}${a.time}`.localeCompare(`${b.date}${b.time}`)
    );
    const header = "Data;Horário;Cliente;Telefone;Serviço";
    const lines = rows.map((b) => {
      const [y, m, d] = b.date.split("-");
      return `${d}/${m}/${y};${b.time};${b.name};${b.phone};${b.service}`;
    });
    const csv = "﻿" + [header, ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `producao-${monthKey(histMonth)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success("Planilha do mês baixada!");
  };

  useEffect(() => {
    if (!showAdd) return;
    let active = true;
    setAddSlots(null);
    setAddTime(null);
    axios
      .get(`${API}/bookings/availability`, { params: { date: addDate } })
      .then((res) => {
        if (active) setAddSlots(res.data);
      })
      .catch(() => toast.error("Não foi possível carregar os horários."));
    return () => {
      active = false;
    };
  }, [showAdd, addDate]);

  const addBooking = async () => {
    if (!addTime) {
      toast.error("Escolha o horário.");
      return;
    }
    if (addName.trim().length < 2) {
      toast.error("Informe o nome do cliente.");
      return;
    }
    if (addPhone.replace(/\D/g, "").length < 10) {
      toast.error("Informe um WhatsApp válido com DDD.");
      return;
    }
    setAddLoading(true);
    try {
      await axios.post(`${API}/bookings`, {
        name: addName.trim(),
        phone: addPhone,
        date: addDate,
        time: addTime,
        service: "Corte de cabelo",
      });
      toast.success(`Agendado: ${addName.trim()} às ${addTime}.`);
      setShowAdd(false);
      setAddName("");
      setAddPhone("");
      setAddTime(null);
      loadBookings(token, true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Não foi possível agendar.");
      try {
        const res = await axios.get(`${API}/bookings/availability`, { params: { date: addDate } });
        setAddSlots(res.data);
        setAddTime(null);
      } catch {
        /* silencioso */
      }
    } finally {
      setAddLoading(false);
    }
  };

  const groups = useMemo(() => {
    if (!bookings) return [];
    const map = new Map();
    bookings.forEach((b) => {
      if (!map.has(b.date)) map.set(b.date, []);
      map.get(b.date).push(b);
    });
    return [...map.entries()];
  }, [bookings]);

  const histGroups = useMemo(() => {
    if (!histData) return [];
    const map = new Map();
    histData.bookings.forEach((b) => {
      if (!map.has(b.date)) map.set(b.date, []);
      map.get(b.date).push(b);
    });
    return [...map.entries()];
  }, [histData]);

  const todayKey = new Date().toISOString().slice(0, 10);
  const todayCount = bookings ? bookings.filter((b) => b.date === todayKey).length : 0;
  const currentMonthStart = new Date();
  currentMonthStart.setDate(1);
  const canGoNextMonth = histMonth < currentMonthStart;

  if (checking) {
    return (
      <section className="px-6 py-24 text-center">
        <p className="text-sm text-stone-500" data-testid="admin-checking">Verificando acesso...</p>
      </section>
    );
  }

  if (!token) {
    return (
      <section className="px-6 py-16" data-testid="admin-login">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        >
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full border border-amber-500/30 bg-amber-400/5">
            <Lock size={26} className="text-amber-400" />
          </div>
          <h2 className="mt-6 text-center font-display text-3xl text-amber-100">
            Painel do <span className="gold-text italic">Barbeiro</span>
          </h2>
          <p className="mt-2 text-center text-xs uppercase tracking-[0.25em] text-stone-500">
            Área restrita
          </p>

          <div className="mt-8 space-y-5">
            <label className="block">
              <span className="mb-2 block text-xs text-stone-500">E-mail</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seu@email.com"
                autoComplete="username"
                className="input-dark"
                data-testid="admin-login-email"
              />
            </label>
            <label className="block">
              <span className="mb-2 block text-xs text-stone-500">Senha</span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && login()}
                placeholder="••••••••"
                autoComplete="current-password"
                className="input-dark"
                data-testid="admin-login-password"
              />
            </label>
          </div>

          <button
            type="button"
            onClick={login}
            disabled={loading}
            className="gold-btn mt-8 w-full"
            data-testid="admin-login-button"
          >
            {loading ? "Entrando..." : "Entrar no painel"}
          </button>
          <a
            href="/"
            className="mt-5 block text-center text-xs uppercase tracking-[0.2em] text-stone-500 transition-colors duration-300 hover:text-amber-300"
            data-testid="admin-back-home"
          >
            Voltar ao site
          </a>
        </motion.div>
      </section>
    );
  }

  return (
    <section className="px-6 py-12" data-testid="admin-dashboard">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-3xl text-amber-100">
            Painel do <span className="gold-text italic">Barbeiro</span>
          </h2>
          <p className="mt-1 text-xs text-stone-500" data-testid="admin-summary">
            {tab === "proximos"
              ? bookings
                ? `${bookings.length} agendamento(s) futuro(s) · ${todayCount} hoje`
                : "Carregando..."
              : histData
                ? `${histData.total} atendimento(s) em ${monthLabelPT(histMonth)}`
                : "Carregando..."}
          </p>
        </div>
        <div className="flex gap-2">
          {tab === "proximos" && (
            <button
              type="button"
              onClick={() => loadBookings()}
              className="rounded-full border border-amber-500/25 p-2.5 text-amber-300 transition-colors duration-300 hover:border-amber-400/60"
              aria-label="Atualizar agenda"
              data-testid="admin-refresh-button"
            >
              <RefreshCw size={16} className={refreshing ? "animate-spin" : ""} />
            </button>
          )}
          <button
            type="button"
            onClick={logout}
            className="rounded-full border border-amber-500/25 p-2.5 text-stone-400 transition-colors duration-300 hover:border-red-400/50 hover:text-red-300"
            aria-label="Sair"
            data-testid="admin-logout-button"
          >
            <LogOut size={16} />
          </button>
        </div>
      </div>

      <div className="mt-7 grid grid-cols-2 gap-2 rounded-2xl border border-amber-500/15 bg-black/30 p-1.5">
        <button
          type="button"
          onClick={() => setTab("proximos")}
          className={`flex items-center justify-center gap-2 rounded-xl py-2.5 text-xs font-semibold uppercase tracking-[0.15em] transition-[background-color,color] duration-300 ${
            tab === "proximos"
              ? "bg-gradient-to-b from-[#E9C767] to-[#B38728] text-black"
              : "text-stone-400 hover:text-amber-200"
          }`}
          data-testid="admin-tab-proximos"
        >
          <Scissors size={14} /> Próximos
        </button>
        <button
          type="button"
          onClick={() => setTab("historico")}
          className={`flex items-center justify-center gap-2 rounded-xl py-2.5 text-xs font-semibold uppercase tracking-[0.15em] transition-[background-color,color] duration-300 ${
            tab === "historico"
              ? "bg-gradient-to-b from-[#E9C767] to-[#B38728] text-black"
              : "text-stone-400 hover:text-amber-200"
          }`}
          data-testid="admin-tab-historico"
        >
          <History size={14} /> Histórico
        </button>
      </div>

      {tab === "proximos" && (
        <>
          <button
            type="button"
            onClick={() => setShowAdd((v) => !v)}
            className={`mt-6 flex w-full items-center justify-center gap-2 rounded-2xl border px-5 py-3.5 text-sm font-semibold transition-[background-color,border-color,color] duration-300 ${
              showAdd
                ? "border-amber-400/60 bg-amber-400/10 text-amber-200"
                : "border-amber-500/25 text-amber-300 hover:border-amber-400/50 hover:bg-amber-400/5"
            }`}
            data-testid="admin-add-button"
          >
            <Plus size={17} /> Agendar horário manualmente
          </button>

          {showAdd && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="mt-4 rounded-2xl border border-amber-500/20 bg-white/[0.02] p-5"
              data-testid="admin-add-form"
            >
              <label className="block">
                <span className="mb-2 block text-xs text-stone-500">Dia</span>
                <input
                  type="date"
                  value={addDate}
                  min={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => e.target.value && setAddDate(e.target.value)}
                  className="input-dark"
                  style={{ colorScheme: "dark" }}
                  data-testid="admin-add-date"
                />
              </label>

              <p className="mb-2 mt-5 block text-xs text-stone-500">Horário</p>
              {!addSlots ? (
                <div className="grid grid-cols-4 gap-2">
                  {Array.from({ length: 8 }).map((_, i) => (
                    <div key={i} className="h-10 animate-pulse rounded-lg border border-amber-500/10 bg-white/[0.03]" />
                  ))}
                </div>
              ) : addSlots.slots.length === 0 ? (
                <p className="rounded-xl border border-stone-800 py-3 text-center text-sm italic text-stone-500" data-testid="admin-add-closed">
                  Fechado neste dia
                </p>
              ) : (
                <div className="grid grid-cols-4 gap-2">
                  {addSlots.slots.map((slot) => {
                    const taken = addSlots.booked.some((b) => b.time === slot);
                    const active = slot === addTime;
                    return (
                      <button
                        key={slot}
                        type="button"
                        disabled={taken}
                        onClick={() => setAddTime(slot)}
                        className={`rounded-lg border py-2.5 font-mono text-xs transition-[background-color,border-color,color] duration-300 ${
                          taken
                            ? "cursor-not-allowed border-stone-800/60 text-stone-700 line-through"
                            : active
                              ? "border-transparent bg-gradient-to-b from-[#E9C767] to-[#B38728] font-bold text-black"
                              : "border-amber-500/25 text-amber-100 hover:border-amber-400/60"
                        }`}
                        data-testid={`admin-add-slot-${slot.replace(":", "-")}`}
                      >
                        {slot}
                      </button>
                    );
                  })}
                </div>
              )}

              <div className="mt-5 space-y-4">
                <label className="block">
                  <span className="mb-2 block text-xs text-stone-500">Nome do cliente</span>
                  <input
                    type="text"
                    value={addName}
                    onChange={(e) => setAddName(e.target.value)}
                    placeholder="Nome completo"
                    className="input-dark"
                    data-testid="admin-add-name"
                  />
                </label>
                <label className="block">
                  <span className="mb-2 block text-xs text-stone-500">Telefone (WhatsApp)</span>
                  <input
                    type="tel"
                    value={addPhone}
                    onChange={(e) => setAddPhone(maskPhone(e.target.value))}
                    placeholder="(13) 99999-9999"
                    className="input-dark"
                    data-testid="admin-add-phone"
                  />
                </label>
              </div>

              <button
                type="button"
                onClick={addBooking}
                disabled={addLoading}
                className="gold-btn mt-6 w-full !py-3.5 !text-base"
                data-testid="admin-add-submit"
              >
                {addLoading ? "Agendando..." : "Confirmar agendamento"}
              </button>
            </motion.div>
          )}

          {!bookings ? (
            <div className="mt-10 space-y-3" data-testid="admin-loading">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-16 animate-pulse rounded-xl border border-amber-500/10 bg-white/[0.03]" />
              ))}
            </div>
          ) : groups.length === 0 ? (
            <div className="mt-14 text-center" data-testid="admin-empty-state">
              <p className="font-display text-xl text-amber-100">Agenda vazia</p>
              <p className="mt-2 text-sm text-stone-500">Nenhum agendamento futuro por enquanto.</p>
            </div>
          ) : (
            <div className="mt-8 space-y-8">
              {groups.map(([date, items]) => (
                <div key={date} data-testid={`admin-day-${date}`}>
                  <p className="mb-3 flex items-center justify-between border-b border-amber-500/15 pb-2">
                    <span className="font-display text-lg text-amber-200">{fullDate(date)}</span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">
                      {items.length} horário(s)
                    </span>
                  </p>
                  <div className="space-y-2.5">
                    {items.map((b) => (
                      <div
                        key={b.id}
                        className="flex items-center gap-3 rounded-xl border border-amber-500/15 bg-white/[0.02] px-4 py-3"
                        data-testid={`admin-booking-${b.id}`}
                      >
                        <span className="w-12 shrink-0 font-mono text-sm font-bold text-amber-400">
                          {b.time}
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-amber-100">{b.name}</p>
                          <p className="text-xs text-stone-500">{b.service}</p>
                        </div>
                        <a
                          href={waLink(b.phone)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="rounded-full border border-[#25D366]/40 p-2 text-[#25D366] transition-colors duration-300 hover:bg-[#25D366]/10"
                          aria-label={`Chamar ${b.name} no WhatsApp`}
                          data-testid={`admin-wa-${b.id}`}
                        >
                          <WhatsAppIcon size={15} />
                        </a>
                        <button
                          type="button"
                          onClick={() => cancelBooking(b)}
                          className="rounded-full border border-red-500/30 p-2 text-red-400/80 transition-colors duration-300 hover:border-red-400/60 hover:bg-red-500/10 hover:text-red-300"
                          aria-label={`Cancelar agendamento de ${b.name}`}
                          data-testid={`admin-cancel-${b.id}`}
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {tab === "historico" && (
        <div className="mt-8">
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => setHistMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1))}
              className="rounded-full border border-amber-500/25 p-2 text-amber-300 transition-colors duration-300 hover:border-amber-400/60"
              aria-label="Mês anterior"
              data-testid="admin-history-prev-month"
            >
              <ChevronLeft size={16} />
            </button>
            <p className="font-display text-lg text-amber-200" data-testid="admin-history-month">
              {monthLabelPT(histMonth)}
            </p>
            <button
              type="button"
              disabled={!canGoNextMonth}
              onClick={() => setHistMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1))}
              className="rounded-full border border-amber-500/25 p-2 text-amber-300 transition-colors duration-300 hover:border-amber-400/60 disabled:opacity-30"
              aria-label="Próximo mês"
              data-testid="admin-history-next-month"
            >
              <ChevronRight size={16} />
            </button>
          </div>

          <div
            className="mt-5 flex items-center justify-center gap-2 rounded-2xl border border-amber-500/20 bg-amber-400/5 py-4"
            data-testid="admin-history-total"
          >
            <Scissors size={16} className="text-amber-400" />
            <p className="text-sm text-amber-100">
              <span className="font-display text-xl font-bold text-amber-300">
                {histLoading ? "…" : histData?.total ?? 0}
              </span>{" "}
              atendimento(s) no mês
            </p>
          </div>

          <button
            type="button"
            onClick={exportMonth}
            className="mx-auto mt-4 flex items-center gap-2 rounded-full border border-amber-500/30 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-amber-200 transition-[background-color,border-color] duration-300 hover:border-amber-400/60 hover:bg-amber-400/10"
            data-testid="admin-export-csv"
          >
            <Download size={15} /> Baixar planilha do mês
          </button>

          {histLoading ? (
            <div className="mt-6 space-y-3" data-testid="admin-history-loading">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-16 animate-pulse rounded-xl border border-amber-500/10 bg-white/[0.03]" />
              ))}
            </div>
          ) : histGroups.length === 0 ? (
            <div className="mt-12 text-center" data-testid="admin-history-empty">
              <p className="font-display text-xl text-amber-100">Nada por aqui</p>
              <p className="mt-2 text-sm text-stone-500">Nenhum atendimento neste mês.</p>
            </div>
          ) : (
            <div className="mt-8 space-y-8">
              {histGroups.map(([date, items]) => {
                const isFuture = date > todayKey;
                return (
                  <div key={date} data-testid={`admin-history-day-${date}`}>
                    <p className="mb-3 flex items-center justify-between border-b border-amber-500/15 pb-2">
                      <span className={`font-display text-lg ${isFuture ? "text-amber-200" : "text-stone-400"}`}>
                        {fullDate(date)}
                      </span>
                      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">
                        {items.length} atendimento(s){isFuture ? " · agendado" : ""}
                      </span>
                    </p>
                    <div className="space-y-2.5">
                      {items.map((b) => (
                        <div
                          key={b.id}
                          className={`flex items-center gap-3 rounded-xl border px-4 py-3 ${
                            isFuture
                              ? "border-amber-500/15 bg-white/[0.02]"
                              : "border-stone-800/60 bg-white/[0.01]"
                          }`}
                          data-testid={`admin-history-booking-${b.id}`}
                        >
                          <span className={`w-12 shrink-0 font-mono text-sm font-bold ${isFuture ? "text-amber-400" : "text-stone-500"}`}>
                            {b.time}
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className={`truncate text-sm font-medium ${isFuture ? "text-amber-100" : "text-stone-400"}`}>
                              {b.name}
                            </p>
                            <p className="text-xs text-stone-600">{b.service}</p>
                          </div>
                          <a
                            href={waLink(b.phone)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="rounded-full border border-[#25D366]/40 p-2 text-[#25D366] transition-colors duration-300 hover:bg-[#25D366]/10"
                            aria-label={`Chamar ${b.name} no WhatsApp`}
                            data-testid={`admin-history-wa-${b.id}`}
                          >
                            <WhatsAppIcon size={15} />
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <p className="mt-10 text-center text-[11px] leading-relaxed text-stone-600">
            O histórico conta apenas atendimentos que não foram cancelados.
          </p>
        </div>
      )}
    </section>
  );
}
